// src/adminDashboard/shared/components/DataTable/MobileCard.tsx
// Mobile card view for DataTable

import { ChevronRight } from 'lucide-react';
import React from 'react';

import { ColumnDef } from './utils/exportHelpers';

const toRecord = (row: object): Record<string, unknown> => row as Record<string, unknown>;

interface MobileCardProps<T extends object = object> {
    row: T;
    columns: ColumnDef[];
    onRowClick?: ((row: T) => void) | undefined;
    rowActions?: ((row: T) => React.ReactNode) | undefined;
    isSelected?: boolean | undefined;
    onSelectRow?: ((rowId: string | number) => void) | undefined;
    getId: (row: T) => string | number;
}

export const MobileCard = <T extends object = object,>({
    row,
    columns,
    onRowClick,
    rowActions,
    isSelected,
    onSelectRow,
    getId
}: MobileCardProps<T>) => {
    const rowId = getId(row);

    return (
        <div
            className={`bg-white rounded-lg border transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'
                }`}
        >
            {/* Selection Checkbox - Outside button to avoid nesting interactive elements */}
            {onSelectRow && (
                <div className="flex items-center gap-3 p-4 pb-3 border-b border-slate-100">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                            e.stopPropagation();
                            onSelectRow(rowId);
                        }}
                        className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary focus:ring-2"
                    />
                    <span className="text-xs font-medium text-slate-500">
                        {isSelected ? 'Selected' : 'Select'}
                    </span>
                </div>
            )}

            {/* Main Interactive Area */}
            {onRowClick ? (
                <button
                    type="button"
                    className="w-full text-left p-4 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary rounded-lg"
                    onClick={() => onRowClick(row)}
                >
                    <CardContent columns={columns} row={row} />
                    {!rowActions && (
                        <div className="flex items-center justify-end mt-3 pt-3 border-t border-slate-100">
                            <span className="text-xs text-primary flex items-center gap-1">
                                View Details
                                <ChevronRight size={14} />
                            </span>
                        </div>
                    )}
                </button>
            ) : (
                <div className="p-4">
                    <CardContent columns={columns} row={row} />
                </div>
            )}

            {/* Row Actions - Outside button to avoid nesting interactive elements */}
            {rowActions && (
                <div className="flex items-center justify-between gap-2 p-4 pt-4 border-t border-slate-100">
                    <span className="text-xs text-slate-500">Actions</span>
                    <div>
                        {rowActions(row)}
                    </div>
                </div>
            )}
        </div>
    );
};

const CardContent = <T extends object>({ columns, row }: { columns: ColumnDef[], row: T }) => (
    <div className="space-y-3">
        {columns.map((column) => {
            // Skip checkbox column
            if (column.key === 'checkbox') return null;

            return (
                <div key={column.key} className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-500 mb-1">
                            {column.header}
                        </p>
                        <div className="text-sm text-slate-900">
                            {(() => {
                                if (column.render) return column.render(row, 0);

                                const value = toRecord(row)[column.key];
                                if (typeof value === 'object' && value !== null) {
                                    return JSON.stringify(value);
                                }
                                return String(value ?? '');
                            })()}
                        </div>
                    </div>
                </div>
            );
        })}
    </div>
);

export default MobileCard;
